import { CSSProperties, forwardRef } from 'react'

import { cn } from '@/utils'

export interface TIconProps {
  name: string
  color?: string
  /**
   * Icon size
   * - `xs`    → 12px
   * - `sm`    → 14px
   * - `md`    → 18px
   * - `md-lg` → 19px
   * - `lg`    → 18px
   * - `xl`    → 21px
   * - `2xl`   → 24px
   */
  size?: 'xs' | 'sm' | 'md' | 'md-lg' | 'lg' | 'xl' | '2xl'
  role?: 'img' | 'presentation'
  ariaLabel?: string
  onClick?: () => void
  ariaHidden?: boolean
  cursor?: React.CSSProperties['cursor']
}

/**
 * @deprecated Use `Icon` from `@alternative/acqua/atoms` instead for display.
 * For the interactive `onClick` button mode, use acqua's `Button` (`size="icon"`)
 * with an `Icon` child instead — acqua has no clickable-icon primitive.
 */
export const Icon = forwardRef<HTMLButtonElement | HTMLSpanElement, TIconProps>(
  (
    {
      name,
      color = 'none',
      size = 'md',
      role = 'img',
      ariaLabel = 'icon',
      onClick,
      ariaHidden = false,
      cursor,
      ...props
    },
    ref
  ) => {
    const isInteractive = !!onClick
    const sizeConfig = {
      xs: { fontSize: '!text-[12px]', dimension: 'w-3 h-3' }, // 12px
      sm: { fontSize: '!text-body-md', dimension: 'w-3.5 h-3.5' }, // 14px
      md: {
        // TODO this should be change based on the design system, for now I just change it based on the design for the Invoice Info Icons
        fontSize: '!text-[18px] 4xl:!text-[22.5px]',
        dimension: 'w-6 h-6 4xl:w-[30px] 4xl:h-[30px]'
      }, // 16px
      'md-lg': { fontSize: '!text-[19px]', dimension: 'w-[19px] h-[19px]' }, // 19px
      lg: { fontSize: '!text-body-xl', dimension: 'w-[18px] h-[18px]' }, // 18px
      xl: {
        fontSize: '!text-[21px] 3xl:!text-[24px] 4xl:!text-[30px]',
        dimension:
          'w-[21px] h-[21px] 3xl:w-[24px] 3xl:h-[24px] 4xl:w-[30px] 4xl:h-[30px]'
      }, // 21px
      '2xl': { fontSize: '!text-[24px]', dimension: 'w-6 h-6' } // 24px
    }

    const commonClasses = cn(
      'flex items-center justify-center leading-none',
      color?.startsWith('#') ? 'text-[var(--icon-color)]' : `text-${color}`,
      sizeConfig[size].dimension,
      cursor && `cursor-${cursor}`
    )

    const commonStyle = color?.startsWith('#')
      ? {
          ...(props && 'style' in props ? (props.style as CSSProperties) : {}),
          ['--icon-color' as keyof CSSProperties]: color
        }
      : props && 'style' in props
      ? (props.style as CSSProperties)
      : {}

    const iconElement = (
      <span
        className={cn(
          'material-symbols-rounded flex h-full w-full items-center justify-center leading-none',
          sizeConfig[size].fontSize
        )}
      >
        {name}
      </span>
    )

    if (!name) {
      return null
    }

    if (isInteractive) {
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          aria-label={ariaLabel}
          aria-hidden={ariaHidden}
          className={commonClasses}
          role={role}
          type='button'
          onClick={onClick}
          {...props}
          style={commonStyle}
        >
          {iconElement}
        </button>
      )
    }

    return (
      <span
        ref={ref as React.Ref<HTMLSpanElement>}
        className={commonClasses}
        role={role}
        aria-label={ariaLabel}
        aria-hidden={ariaHidden}
        {...props}
        style={commonStyle}
      >
        {iconElement}
      </span>
    )
  }
)

Icon.displayName = 'Icon'
